import { Element, Node } from '../core';
import { LIST_PLUGIN_ID } from '../plugins/list/types';
import { PARAGRAPH_PLUGIN_ID } from '../plugins/paragraph/types';
import { LIST_VARIANTS } from '../plugins/list/constants';

export const flattenTopLevelListNodesToParagraphNodes = (fragment: Array<Node>): Array<Node> => {
  const flattenListNode = (node: Node): Array<Node> => {
    if (!Element.isElement(node) || node.type !== LIST_PLUGIN_ID || !Array.isArray(node.children)) {
      return [node];
    }

    const flattenedChildren: Array<Node> = [];
    node.children.forEach((child) => {
      if (Element.isElement(child) && child.type === LIST_PLUGIN_ID) {
        flattenedChildren.push(...flattenListNode(child));
        return;
      }

      if (
        Element.isElement(child) &&
        child.type === PARAGRAPH_PLUGIN_ID &&
        child.variant === LIST_VARIANTS.li
      ) {
        const { variant: _variant, ...paragraphNodeWithoutListVariant } = child;
        flattenedChildren.push(paragraphNodeWithoutListVariant);
        return;
      }

      flattenedChildren.push(child);
    });

    return flattenedChildren;
  };

  const flattenedFragment: Array<Node> = [];
  fragment.forEach((node) => {
    if (Element.isElement(node) && node.type === LIST_PLUGIN_ID) {
      flattenedFragment.push(...flattenListNode(node));
      return;
    }

    flattenedFragment.push(node);
  });

  return flattenedFragment;
};

export const countListItemsInFragment = (fragment: Array<Node>): number => {
  const countListItemsInNode = (node: Node): number => {
    if (!Element.isElement(node) || !Array.isArray(node.children)) {
      return 0;
    }

    if (node.type === PARAGRAPH_PLUGIN_ID && node.variant === LIST_VARIANTS.li) {
      return 1;
    }

    return node.children.reduce((accumulator, child) => {
      return accumulator + countListItemsInNode(child);
    }, 0);
  };

  return fragment.reduce((accumulator, node) => {
    return accumulator + countListItemsInNode(node);
  }, 0);
};

export const convertListNodesToPlainTextParagraphs = (fragment: Array<Node>): Array<Node> => {
  const getListItemTexts = (node: Node): Array<string> => {
    if (!Element.isElement(node)) {
      return [];
    }

    if (node.type === PARAGRAPH_PLUGIN_ID && node.variant === LIST_VARIANTS.li) {
      return [Node.string(node)];
    }

    if (!Array.isArray(node.children)) {
      return [];
    }

    return node.children.flatMap((child) => getListItemTexts(child));
  };

  return fragment.flatMap((node) => {
    if (Element.isElement(node) && node.type === LIST_PLUGIN_ID) {
      const listItemTexts = getListItemTexts(node);
      return listItemTexts.map((text) => ({
        type: PARAGRAPH_PLUGIN_ID,
        children: [{ text }],
      }));
    }

    return [node];
  });
};
